import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import styled, { css } from "styled-components";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { findCuepoint, isCuepointVisible } from "../../helpers/cuepoints";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { buttonStyle } from "../../styles/global";
import { focusColor } from "../../styles/variables";
import { Cuepoint } from "../../types/player-api";
import { skipSegment } from "../skipButton/skipButtonHelpers";

const SkipButtonStyled = styled.button<{
  buttonType: "intro" | "outro";
  isVisible: boolean;
}>`
  ${buttonStyle};
  background-color: ${darkTheme.BACKGROUND_DIALOG_OVERLAY};
  border-radius: 18px;
  bottom: 5rem;
  color: ${darkTheme.TEXT_DEFAULT};
  font-size: 18px;
  font-weight: bold;
  height: 36px;
  left: ${({ buttonType }) => (buttonType === "intro" ? "3rem" : "initial")};
  min-width: 36px;
  opacity: ${({ isVisible }) => (isVisible ? "50%" : 0)};
  padding: 0 0.5rem;
  position: absolute;
  right: ${({ buttonType }) => (buttonType === "outro" ? "3rem" : "initial")};
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};
  z-index: 11;

  ${({ isVisible }) =>
    isVisible
      ? css`
          transition:
            opacity 0.5s,
            visibility 0s;
        `
      : css`
          transition:
            opacity 0.5s,
            visibility 0s linear 0.5s;
        `}

  &:focus-visible {
    opacity: 1;
    transition: opacity 0s;
    outline: 1px solid ${focusColor} !important;
  }
  &:hover {
    opacity: 1;
    transition: opacity 0s;
  }
  &:active {
    background-color: ${darkTheme.BACKGROUND};
    opacity: 1;
  }
`;

type Props = {
  buttonType: "intro" | "outro";
  cuepoints: Cuepoint[];
  UIRef?: HTMLDivElement | null;
  started: boolean;
};

export const SkipButton: FC<Props> = ({
  UIRef,
  buttonType,
  cuepoints,
  started
}) => {
  const { subscribe, useStoreState } = usePlayerStore();
  const durationInSeconds = useStoreState.durationInSeconds();
  const player = useStoreState.player();

  const { language } = useEmbedParametersContext();

  const [isVisible, setIsVisible] = useState(false);
  const cuepoint = useMemo(
    () => findCuepoint(buttonType, cuepoints),
    [cuepoints, buttonType]
  );

  const onClickButton = useCallback(() => {
    UIRef?.focus();
    setIsVisible(false);
    skipSegment(player.seekTo, durationInSeconds, cuepoint);
  }, [UIRef, cuepoint, durationInSeconds, player]);

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === " ") {
        e.stopPropagation();
        onClickButton();
      }
    },
    [onClickButton]
  );

  useEffect(() => {
    if (!cuepoint || !started) return;

    const unsubscribe = subscribe(
      state => state.currentTime,
      currentTime => {
        setIsVisible(
          isCuepointVisible(started, currentTime, cuepoint, durationInSeconds)
        );
      }
    );

    return () => {
      unsubscribe();
    };
  }, [cuepoint, durationInSeconds, started, subscribe]);

  return (
    <SkipButtonStyled
      aria-hidden={!isVisible}
      buttonType={buttonType}
      onClick={onClickButton}
      onKeyUp={onKeyUp}
      isVisible={isVisible}>
      {i18n(buttonType === "intro" ? "ohita_intro" : "ohita_lopputekstit", {
        language
      })}
    </SkipButtonStyled>
  );
};
